import React, { useRef, useState } from "react";
import { AnimatePresence, LazyMotion, m, domMax, useReducedMotion } from "framer-motion";
import {
  Brain, MessageCircle, IndianRupee, ShieldCheck, BarChart3, PenLine, Sunrise,
  Workflow, Lock, Sigma, SlidersHorizontal, ArrowRight, Bot, Languages,
  Building2, GraduationCap, Users, BookOpen, ClipboardCheck, LineChart,
  ScanSearch, Landmark, Mic, Globe, History, Zap, GitBranch,
  ChevronDown, Check, Sparkles,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/primitives";
import { Reveal, EASE } from "./parts1";
import { smoothScrollToId } from "./parts4";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { CTA_PRIMARY_LABEL } from "../../components/marketing/navigation";

/* ────────────────────────────────────────────────────────────────────────────
   AiSuiteSection — the "AI & Automation" showcase. Mounts DIRECTLY AFTER
   AiraAISection so the story reads: "chat with Aira" → "and Aira runs these
   jobs for you". LIGHT (bg-surface) to alternate with the muted sections
   around it. ALL accents use the `ai` purple token (never the brand blue).
   Every claim is grounded in docs/product/FEATURE_INVENTORY.md §16 (AI/Aira)
   + §13 (automation engine); buyer framing from docs/marketing/
   MARKETING_CONTENT.md §4 "The AI story".

   Anatomy (mirrors the #modules explorer — the section's sibling):
   header → settled "Aira right now" example → WAI-ARIA persona tablist
   (sliding purple pill) → a per-tab grounded fact strip → a keyed tab panel
   (staggered card grid + a per-persona guardrails rail) → trust footnotes →
   CTAs. Each card carries its Lucide glyph in a soft purple-tinted chip
   (the SAME 52px chip anatomy as the #modules feature cards — one icon
   language, purple for AI), and is CLICK-TO-EXPAND in place — a real <h3><button
   aria-expanded> (the house FaqItem accordion pattern) stretched over the
   card, revealing "How it works" (2–3 sentences from §16's real behavior),
   a 3-item "What you get" list, and a grounded plan/governance note. One
   card open per tab; height animates with the house EASE; reduced-motion
   snaps everywhere (pill/panel/expand snap).
   ──────────────────────────────────────────────────────────────────────────── */

/* Illustrative "Aira is working" vignettes — each maps to a real shipped flow
   (dunning · batch report-card comments · WhatsApp Parent Aira · compliance
   scan); counts are illustrative, the behaviors are literal. Editable via the
   Marketing CMS (cms.ai_suite.ticker); this fallback MUST mirror
   core/marketing.py DEFAULTS["ai_suite"].ticker byte-for-byte. */
const TICKER = [
  "Illustrative run · scored overdue accounts and prepared a human-reviewed reminder queue",
  "Illustrative run · drafted board-aware report-card remarks for a class teacher to review",
  "Illustrative run · answered a parent in Telugu, using only their own child's records",
  "Illustrative run · flagged APAAR-consent gaps and explained the deterministic checks",
];

/* Persona tabs. Each card: { icon (Lucide glyph — rendered in the purple
   chip, same chip anatomy as the #modules feature cards), title,
   desc, badge, how (2–3 sentences of real mechanics from §16/§13), get
   (3 concrete outcomes), note (grounded plan-tier / governance line) }.
   Badges are a fixed grounded vocabulary — VOICE (Sarvam voice in/out) ·
   SCOPED (the I-08 permission/redaction-controls story) · AUTO (runs on schedule/
   trigger) · AGENT (a typed drafting agent a human reviews). strip: the
   per-tab factual number strip. rail: per-persona guardrail rows + badge. */
const PERSONA_TABS = [
  {
    k: "office", label: "Front office & principal", icon: Building2, cols: 3,
    strip: ["6 AI copilots", "supported figures computed by product engines"],
    cards: [
      {
        icon: MessageCircle, badge: "Voice",
        title: "Aira staff chat — voice in, voice out",
        desc: "Ask about the school records you already have permission to see — by text, or by voice in 11 languages once the voice provider is set up.",
        how: "Ask by text or, when the voice provider is configured, by voice in 11 supported languages and hear the answer read aloud. Aira routes supported questions through permission-checked tools. The default router pattern-redacts common identifier formats; known names can be tokenised when the calling flow supplies them, so safeguards must be reviewed for each enabled channel.",
        get: [
          "Supported answers: student counts, fees outstanding, today's attendance, open tasks",
          "Configured voice in and read-aloud out, in 11 supported languages",
          "Safe write-actions: Aira proposes, a human confirms, then it executes",
        ],
        note: "Included with the Aira assistant tier · metered as credits",
      },
      {
        icon: IndianRupee, badge: "Agent",
        title: "AI fee recovery",
        desc: "Scores eligible overdue accounts, prepares a five-step recovery ladder and checks invoice state before each configured reminder.",
        how: "Eligible overdue accounts are scored from the school's payment history using configured feature safeguards. Reminders can climb a five-step ladder (gentle → follow-up → firm → final → a human call-task), with tone and channel matched to risk. Each run re-checks invoice state so a paid invoice is removed from the active ladder.",
        get: [
          "A pay-propensity score (0–100) for each eligible overdue invoice",
          "AI-personalised WhatsApp/SMS reminders with a pay link",
          "Honest KPIs — recovered vs waived vs voided, never inflated",
        ],
        note: "Included with the Automation tier · credit-metered",
      },
      {
        icon: ShieldCheck, badge: "Role-limited",
        title: "Compliance AI",
        desc: "Pre-submission DPDP, UDISE+ and APAAR scans with plain-language fixes and rule citations — before the deadline finds you.",
        how: "A deterministic rulebook of 13 checks scans eligible records for UDISE+, APAAR and DPDP gaps — a missing DOB, guardian mobile or PEN, or a consent gap — and rolls them into school-wide readiness scores. Ask “explain” on a supported finding and the AI receives the rule and aggregate count, not the student row, then returns reviewable fix steps.",
        get: [
          "UDISE+ / APAAR readiness % at a glance",
          "Why-it-matters + fix steps, with the rule cited",
          "Consent-gap and format checks before the deadline",
        ],
        note: "The scan is deterministic & free · AI explain uses Aira credits",
      },
      {
        icon: BarChart3, badge: "Role-limited",
        title: "Talk to your data",
        desc: "“How many Class XI students have pending fees?” Plain-English answers from live school data — the model picks a report from a fixed list, it never writes the query.",
        how: "Your question is routed to one report in a fixed, vetted catalogue — the AI never writes a database query. The report runs scoped to your school, then the AI narrates the result over a digest with every name and contact column stripped out. The full table, names included, renders only in your browser — it never reaches the model.",
        get: [
          "Plain-English questions over the vetted report catalogue",
          "Every figure computed by the report engine, not the AI",
          "Amounts always in real ₹ — never approximations",
        ],
        note: "Included with the Aira assistant tier",
      },
      {
        icon: Sunrise, badge: "Auto",
        title: "The principal's morning briefing",
        desc: "Attendance, at-risk students, fee collections and open tasks, distilled into one daily “what needs you today”.",
        how: "Each morning the platform computes the day's numbers deterministically — attendance, at-risk students, fee collection, open tasks — and the AI only writes the words around them. The result is one scannable “what needs you today”. With no AI key you still get a clean numbers-only summary — never a blank page.",
        get: [
          "One daily digest instead of five dashboards",
          "At-risk flags from the same engine as the Insights console",
          "The numbers still arrive as a plain summary when AI prose is unavailable",
        ],
        note: "Numbers computed, AI writes prose only",
      },
      {
        icon: Landmark, badge: "Role-limited",
        title: "Finance AI",
        desc: "Duplicate-payment and outlier alerts, a weekly cash-flow forecast, bank-to-ledger reconciliation — and “ask about your books” in plain English.",
        how: "Duplicate payments and outliers are caught with median-based statistics — deterministic, free, no AI required. The weekly cash-flow forecast projects your position from outstanding dues, your real collection rate and the expense run-rate. “Ask about your books” answers over an aggregate digest — totals and category names, never a payer or payee name.",
        get: [
          "Duplicate-payment & outlier anomaly alerts",
          "A weekly cash-flow forecast + bank-to-ledger reconciliation",
          "Plain-English Q&A over your books",
        ],
        note: "Detection & reconciliation free · Q&A uses Aira credits",
      },
    ],
    rail: {
      rows: [
        [Bot, "6 office AI consoles", "fees · finance · compliance · analytics"],
        [Languages, "Voice in, voice out", "11 languages, spoken & read aloud"],
        [Sigma, "Numbers work with AI off", "core figures remain available without generated prose"],
      ],
      badge: "Governed & credit-metered",
    },
  },
  {
    k: "teachers", label: "Teachers", icon: GraduationCap, cols: 3,
    strip: ["5 assistive agents", "teachers review — AI never auto-publishes"],
    cards: [
      {
        icon: PenLine, badge: "Agent",
        title: "AI-assisted report-card remarks",
        desc: "Board-aware remarks — CBSE, ICSE, state boards, NEP HPC — drafted from each student's real marks. Teachers review, not type.",
        how: "The agent reads permitted marks and attendance inputs plus your school's board configuration — CBSE 8-point, ICSE, state boards or the NEP Holistic Progress Card — and drafts a remark for teacher review. Configured identifier checks run on the request path; the class teacher reviews and signs off before publication.",
        get: [
          "Board-aware remarks + strengths & focus areas",
          "Whole-class batch drafting in one click",
          "Optional translation into the parent's language",
        ],
        note: "AI drafts, the teacher approves — nothing auto-publishes",
      },
      {
        icon: BookOpen, badge: "Agent",
        title: "Lesson plans & question papers",
        desc: "Drafted from the curriculum your school has adopted — not the open internet — then reviewed and edited by the teacher.",
        how: "Generation grounds on the curriculum text YOUR school has adopted — uploaded once, retrieved per topic — not the open internet. Teachers get an editable draft aligned to what the class is actually studying, ready to refine and use.",
        get: [
          "Lesson plans grounded in your own curriculum",
          "Question papers matched to your syllabus",
          "Fully editable before anything is used",
        ],
        note: "Included with the Aira assistant tier · credit-metered",
      },
      {
        icon: ClipboardCheck, badge: "Agent",
        title: "Essay scoring, rubrics & reading levels",
        desc: "Score essays against a rubric, generate the rubric itself, and re-level any passage for mixed-ability readers.",
        how: "Score a stack of essays against a rubric with per-criterion feedback — or have the AI generate the rubric itself. The text-leveler rewrites any passage for mixed-ability or second-language readers. Everything is assistive: the teacher reviews every output before it counts.",
        get: [
          "Rubric-based scores with written feedback",
          "One-click rubric generation",
          "Re-level any passage for any reading level",
        ],
        note: "Assistive drafts — the teacher stays in charge",
      },
      {
        icon: LineChart, badge: "Role-limited",
        title: "Exam analysis",
        desc: "Class marks distilled into where students struggled and what to reteach — every statistic computed, never guessed.",
        how: "Class statistics — the distribution, the toughest questions, section gaps — are computed from the marks themselves; the AI turns them into “here's where they struggled, here's what to reteach”. Every statistic is real; the AI only interprets it.",
        get: [
          "Where the class struggled, computed from real marks",
          "Concrete reteach suggestions",
          "Section-scoped — you analyse your own classes",
        ],
        note: "Every statistic computed, never guessed",
      },
      {
        icon: ScanSearch, badge: "Role-limited",
        title: "Student snapshot & risk-explain",
        desc: "A 60–90-word “who is this learner” narrative, plus a plain-language reason behind every at-risk flag — with suggested next steps.",
        how: "The at-risk score is a deterministic multi-factor computation over configured attendance, marks, fee and discipline inputs, reused from the Insights console. Aira adds a plain-language explanation and suggested next steps. This student-risk tool context excludes health, infirmary and counselling records.",
        get: [
          "A 60–90-word “who is this learner” narrative",
          "The why behind every at-risk flag, plus next steps",
          "Health, infirmary and counselling inputs excluded from this AI context",
        ],
        note: "Section-scoped: a 5A teacher can't ask about 6B",
      },
    ],
    rail: {
      rows: [
        [Bot, "AI drafts, the teacher signs", "nothing ships without review"],
        [BookOpen, "Grounded in your board", "CBSE · ICSE · state · NEP HPC"],
        [Lock, "Section-scoped by design", "a 5A teacher can't ask about 6B"],
      ],
      badge: "A teacher reviews and signs off before anything is saved or published",
    },
  },
  {
    k: "families", label: "Parents & students", icon: Users, cols: 2,
    strip: ["own-child-only by construction", "11 languages, text & voice"],
    cards: [
      {
        icon: MessageCircle, badge: "Role-limited",
        title: "Parent Aira — about your own child",
        desc: "Fees, attendance, results and the bus — answered from live data on web, by voice, even as a WhatsApp reply. Never another family's child.",
        how: "Answers are scoped to your own children by construction — Aira's tools can only look up the student IDs on your account, so another family's records are unreachable. The default router pattern-redacts common identifiers, and known names can be tokenized when the calling flow supplies them. Works on the web portal, in the early-access app, and as an opt-in WhatsApp auto-reply when configured.",
        get: [
          "Fees, attendance, published marks & the bus — from live data",
          "Web + early-access mobile + opt-in WhatsApp",
          "Role-scoped tools plus configured identifier safeguards",
        ],
        note: "School-controlled: on/off toggle + a protected credit budget",
      },
      {
        icon: Mic, badge: "Voice",
        title: "Vernacular voice, 11 languages",
        desc: "A parent who can't type in English speaks in Hindi, Tamil, Telugu or Odia — and hears the answer read back, out loud, in the same language.",
        how: "Speak your question in any of 11 supported languages — English, Hindi, Bengali, Tamil, Telugu, Odia, Marathi and more — and hear the answer read back. Voice requests use the same configured identifier checks and role-scoped tools as typed requests.",
        get: [
          "Voice input in 11 supported languages",
          "One tap: speak the question, hear the answer",
          "11 languages, in and out",
        ],
        note: "Powered by India's Sarvam voice stack",
      },
      {
        icon: GraduationCap, badge: "Role-limited",
        title: "Student ask-Aira (Class 5+)",
        desc: "Students ask about their own classes, homework and results — behind a school-controlled on/off switch and a shared credit budget.",
        how: "Students from Class 5 up ask about their own classes, homework, attendance and published results — the same own-record boundary as parents. The school holds the switch: student AI can be turned off per persona, and student + parent usage draws from a protected sub-budget that can never drain the staff pool.",
        get: [
          "Own classes, homework & results only",
          "A school-controlled on/off switch",
          "A capped, shared student+parent credit budget",
        ],
        note: "Available Class 5 and above, by policy",
      },
      {
        icon: Globe, badge: "Auto",
        title: "Admission bot on your website",
        desc: "When you switch it on, it answers prospective parents from your approved website content and records each enquiry in your admissions pipeline for staff to follow up.",
        how: "The public-site bot uses approved published site copy and public help content rather than student or fee tools. When the channel and admissions hand-off are enabled, captured enquiries are filed into the CRM with their source for staff review.",
        get: [
          "Public-content answers on the enabled website channel",
          "Captured enquiries handed to the admissions CRM",
          "No student or fee tools in this public-bot context",
        ],
        note: "Rate-limited & metered · owner-toggled per school",
      },
    ],
    rail: {
      rows: [
        [Lock, "Own-child boundary", "a parent sees only their children"],
        [Languages, "11 languages", "text and voice, in and out"],
        [SlidersHorizontal, "School-governed", "per-persona on/off + credit budget"],
      ],
      badge: "Configured privacy controls",
    },
  },
  {
    k: "automations", label: "Automations", icon: Workflow, cols: 2,
    strip: ["40 starter rules", "reviewed action catalogue", "every run audited"],
    cards: [
      {
        icon: Zap, badge: "Auto",
        title: "40 rules, ready to review",
        desc: "Fee and invoice reminders, attendance-shortfall alerts, at-risk interventions, alerts before a vehicle document expires — all ready from day one, and all off until you say go.",
        how: "When Automation is on your plan, the starter library includes fee and invoice reminders, absence WhatsApps to parents, attendance-shortfall board alerts, at-risk interventions and transport document-expiry sweeps. Rules start disabled. Customised copies remain school-owned when the starter library is refreshed.",
        get: [
          "Fee, attendance, CRM, transport & AI early-warning rules",
          "Everything off by default — you choose what runs",
          "Starter rules update without clobbering your edits",
        ],
        note: "Included with the Automation tier",
      },
      {
        icon: GitBranch, badge: "Auto",
        title: "Visual drag-and-drop builder",
        desc: "Build or edit any rule on a full-page canvas — simulate it safely before enabling, with version history and one-click rollback.",
        how: "Build any rule on a full-page drag-and-drop canvas — triggers, nested if/else branches and actions — with undo/redo and keyboard access. Conditions are pure data, never code, so a rule can at most evaluate true or false. Simulate against a sample event before enabling: it previews every action without executing anything.",
        get: [
          "No-code if-this-then-that with nested branching",
          "Simulate safely before you switch a rule on",
          "Version history with one-click rollback",
        ],
        note: "Owner/admin authoring · principals get view + simulate",
      },
      {
        icon: ShieldCheck, badge: "Role-limited",
        title: "Propose → confirm → execute",
        desc: "Before Aira changes anything, it shows you what it plans to do. The system re-checks your permissions, and nothing runs until you confirm.",
        how: "When Aira wants to write — record a payment, mark attendance, post an announcement — it can only propose. The backend re-validates permissions, school scope and the data itself, and sensitive actions always show a preview and wait for a human confirm. Only then does it execute, atomically and idempotently.",
        get: [
          "A human confirm before every sensitive write",
          "Server-side re-validation — the AI can't skip permissions",
          "Payment amounts clamped to the actual due",
        ],
        note: "The AI proposes; only a person approves",
      },
      {
        icon: History, badge: "Auto",
        title: "Every action audited",
        desc: "Each run leaves a per-step audit trail you can drill into — and side-effects stay inside a fixed catalogue of reviewed actions, never open code.",
        how: "Each run writes a per-step audit trail — which rule fired, which action ran, how many attempts, what came back — that you can drill into. Side-effects come only from a fixed catalogue of reviewed actions, and loop-breakers stop any runaway rule chain.",
        get: [
          "Per-run, per-step drill-down history",
          "Reviewed action catalogue — no open code execution",
          "A retry/stop error policy + a timeout on every step",
        ],
        note: "Reviewed actions with per-step audit evidence",
      },
    ],
    rail: {
      rows: [
        [Zap, "40 starter rules", "materialised for every school"],
        [ShieldCheck, "Reviewed action catalogue", "pure-data conditions, no code execution"],
        [History, "Versioned & simulated", "test first, roll back any time"],
      ],
      badge: "Reviewed and auditable",
    },
  },
];

const AUTOMATION_EXAMPLES = [
  "Fee & invoice reminders", "Absence alerts to parents", "Attendance-shortfall board alerts",
  "Admission-enquiry follow-ups", "At-risk student interventions", "Transport document-expiry sweeps",
];

const TRUST_NOTES = [
  [Lock, "Common identifier patterns are redacted by default; supplied known names can be tokenised before supported AI calls."],
  [Sigma, "Supported figures are computed by product engines; AI provides the explanatory text."],
  [SlidersHorizontal, "You control it: per-persona on/off toggles, credit budgets, and an off-switch."],
];

/* stagger for the card grid — recomputed on tab change (crisp, ~60ms apart) */
const PANEL_STAGGER = { hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
const PANEL_ITEM = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.2, ease: EASE } } };

/* A settled example keeps the illustrative context visible without an ambient
   timer, a pulsing status indicator or a hidden hover/focus pause mechanism. */
function AiraTicker({ lines = TICKER }) {
  const line = lines.find((item) => typeof item === "string" && item.trim()) || TICKER[0];

  return (
    <div
      className="inline-flex max-w-full min-w-0 items-center gap-2.5 rounded-full border border-ai/20 bg-ai/[0.05] px-4 py-2"
      data-motion="settled" data-testid="aisuite-ticker">
      <span className="w-1.5 h-1.5 rounded-full bg-ai shrink-0" aria-hidden="true" />
      <span className="text-[11px] font-bold uppercase tracking-wide text-ai-ink shrink-0">Example run</span>
      <span className="min-w-0 truncate text-[13px] text-ink-2">{line}</span>
    </div>
  );
}

/* The card's identity chip: the Lucide glyph in a soft purple-tinted chip —
   the SAME 52px rounded-2xl chip anatomy as the #modules feature cards
   (purple stays the AI accent). Decorative — the title carries meaning. */
function AiIconChip({ Icon }) {
  return (
    <span className="w-[52px] h-[52px] rounded-2xl bg-ai/10 grid place-items-center shrink-0 transition-transform duration-200 ease-out group-hover:scale-105">
      <Icon className="w-7 h-7 text-ai" strokeWidth={2} aria-hidden="true" />
    </span>
  );
}

/* One expandable capability card — Lucide icon chip + grounded badge + outcome-
   first copy, then click-to-expand depth: "How it works" (real mechanics from
   FEATURE_INVENTORY §16/§13), a 3-item "What you get", and a grounded tier/
   governance note. The accessible trigger is the <h3><button aria-expanded>
   (house FaqItem pattern), stretched over the whole card via ::after so the
   card itself is the tap target; height animates like the FAQ (reduced-motion
   snaps). Hover is a pointer enhancement (transform/shadow only). */
function AiCard({ card, open, onToggle, reduce, rid, testid }) {
  const { icon: Icon, title, desc, badge, how, get, note } = card;
  return (
    <m.article
      variants={reduce ? undefined : PANEL_ITEM}
      className={cn(
        "group relative rounded-2xl border bg-surface p-5 cursor-pointer transition-[border-color,transform,box-shadow] duration-200 ease-out",
        open
          ? "border-ai/40 shadow-[0_14px_30px_-14px_rgba(124,58,237,0.35)]"
          : "border-line hover:border-ai/35 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-14px_rgba(124,58,237,0.35)]"
      )}>
      <div className="flex items-start justify-between gap-3 mb-3.5">
        <AiIconChip Icon={Icon} />
        <span className="flex items-center gap-1.5 mt-0.5">
          <span className="inline-flex items-center rounded-md bg-ai/10 px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide text-ai-ink">{badge}</span>
          <span className="w-6 h-6 rounded-lg bg-ai/10 grid place-items-center" aria-hidden="true">
            <ChevronDown className={cn("w-3.5 h-3.5 text-ai transition-transform duration-300", open && "rotate-180")} />
          </span>
        </span>
      </div>
      <h3 className="font-heading font-semibold text-ink text-[15.5px] leading-snug">
        {/* Stretched trigger — ::after covers the card, so clicking anywhere toggles;
            keyboard/AT users get a real button on the title (Enter/Space native). */}
        <button type="button" aria-expanded={open} aria-controls={rid} onClick={onToggle} data-testid={testid}
          className="text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface after:absolute after:inset-0 after:rounded-2xl">
          {title}
        </button>
      </h3>
      <p className="text-ink-2 text-[13px] leading-relaxed mt-1.5">{desc}</p>
      <AnimatePresence initial={false}>
        {open && (
          <m.div id={rid} role="region" aria-label={title} className="overflow-hidden"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={reduce ? undefined : { height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}>
            <div className="mt-3.5 pt-3.5 border-t border-ai/15">
              <p className="text-[10.5px] font-bold uppercase tracking-wide text-ai-ink">How it works</p>
              <p className="text-ink-2 text-[12.5px] leading-relaxed mt-1">{how}</p>
              <p className="text-[10.5px] font-bold uppercase tracking-wide text-ai-ink mt-3">What you get</p>
              <ul className="mt-1.5 space-y-1.5">
                {get.map((g) => (
                  <li key={g} className="flex items-start gap-2 text-[12.5px] text-ink-2 leading-snug">
                    <Check className="w-3.5 h-3.5 text-ai shrink-0 mt-[1px]" aria-hidden="true" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
              {note && (
                <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ai/10 px-2.5 py-1 text-[11px] font-semibold text-ai-ink">
                  <Sparkles className="w-3 h-3" aria-hidden="true" /> {note}
                </p>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </m.article>
  );
}

/* Per-persona guardrails rail — the sibling of the modules section's
   "Module Coverage" panel, purple, content-specific per persona. */
function GuardrailsRail({ rail }) {
  return (
    <div className="rounded-2xl border border-ai/20 bg-ai/[0.04] p-[18px]">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-xl bg-ai/10 grid place-items-center shrink-0">
          <Lock className="w-[18px] h-[18px] text-ai" aria-hidden="true" />
        </span>
        <span className="text-[13px] font-bold text-ink">Aira guardrails</span>
      </div>
      <div className="space-y-3.5">
        {rail.rows.map(([RIcon, t, s]) => (
          <div key={t} className="flex items-start gap-2.5">
            <span className="w-6 h-6 rounded-lg bg-ai/10 grid place-items-center shrink-0 mt-0.5">
              <RIcon className="w-3.5 h-3.5 text-ai" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <span className="block text-[12.5px] font-semibold text-ink leading-tight">{t}</span>
              <span className="block text-[11px] text-ink-subtle">{s}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3.5 pt-3.5 border-t border-ai/15">
        {/* bg-ai/5 (not /10): this chip sits on the double-tinted rail panel — /10 blends the
            backdrop to ~4.46:1 under text-ai-ink, a hair below the 4.5 AA bar */}
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ai/5 px-3 py-1.5 text-[11.5px] font-bold text-ai-ink">
          <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> {rail.badge}
        </span>
      </div>
    </div>
  );
}

/* Props (all optional — defaults render today's site byte-identically):
   `content` = cms.ai_suite ({badge,heading,highlight,subhead,ticker[]}) drives the
   section HEADER + ticker only — the persona cards stay product-grounded, like
   plan_matrix. Heading split: `heading` is the purple text-ai lead span ("One AI."),
   `highlight` the plain-ink tail (NOTE: the reverse of the hero's split). Fallbacks
   MUST mirror core/marketing.py DEFAULTS["ai_suite"]. `seeAiraVisible` gates the
   "See Aira" CTA (its #aira-ai target unmounts when the admin hides aira_demo);
   `onRegister` overrides the register CTA when #register-your-school is hidden. */
function AiSuiteSectionContent({ content, seeAiraVisible = true, onRegister }) {
  const c = content || {};
  const ticker = Array.isArray(c.ticker) && c.ticker.length ? c.ticker : TICKER;
  const prefersReducedMotion = useReducedMotion();
  const { paused: marketingPaused } = useMarketingMotion();
  const reduce = prefersReducedMotion || marketingPaused;
  const [tab, setTab] = useState("office");
  const [expanded, setExpanded] = useState(null); // one open card per tab
  const tabRefs = useRef([]);
  const active = PERSONA_TABS.find((t) => t.k === tab) || PERSONA_TABS[0];
  const selectTab = (k) => { setTab(k); setExpanded(null); };

  /* WAI-ARIA tablist — roving tabindex, Arrow/Home/End (same pattern as the
     #modules explorer's tablist in Landing.js). */
  const onTabKey = (e, idx) => {
    const last = PERSONA_TABS.length - 1;
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = idx === last ? 0 : idx + 1;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = idx === 0 ? last : idx - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    if (next !== null) { e.preventDefault(); selectTab(PERSONA_TABS[next].k); tabRefs.current[next]?.focus(); }
  };

  return (
    <section id="ai-suite" className="campus-glass-surface border-b border-line scroll-mt-24" aria-labelledby="ai-suite-heading">
      <div className="max-w-7xl mx-auto px-5 py-12 md:py-16 lg:py-20">

        {/* heading */}
        <Reveal className="text-center max-w-[820px] mx-auto">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-ai/10 border border-ai/20 text-ai-dark px-3 py-1.5 text-[12.5px] font-semibold mb-4">
            <Brain className="w-3.5 h-3.5" aria-hidden="true" /> {c.badge || "The Aira AI & Automation Suite"}
          </span>
          <h2 id="ai-suite-heading" className="font-heading font-extrabold text-[30px] sm:text-[42px] lg:text-[52px] leading-[1.05] text-ink tracking-tight">
            <span className="text-ai">{c.heading || "One AI."}</span>{" "}{c.highlight || "Every corner of the school."}
          </h2>
          <p className="text-ink-2 mt-3 text-lg leading-relaxed">
            {c.subhead || "Aira works inside fees, compliance, results and the front office — answering from records the asking person is already allowed to see, and proposing actions a person confirms."}
          </p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-5">
            <span className="inline-flex items-center gap-1.5 text-ink-2 text-[12.5px] font-medium"><Bot className="w-3.5 h-3.5 text-ai" aria-hidden="true" /> Governed AI capabilities</span>
            <span className="inline-flex items-center gap-1.5 text-ink-2 text-[12.5px] font-medium"><Languages className="w-3.5 h-3.5 text-ai" aria-hidden="true" /> 11 languages, text &amp; voice</span>
          </div>
          <ol className="mt-7 grid grid-cols-2 gap-2 text-left sm:grid-cols-4" aria-label="How Aira produces a governed answer">
            {[
              ["1", "Ask", "Text, voice or a product workflow"],
              ["2", "Protect", "Permissions checked; configured safeguards applied"],
              ["3", "Compute", "Product engines calculate the real answer"],
              ["4", "Review", "A person confirms every sensitive action"],
            ].map(([n, title, desc]) => (
              <li key={n} className="rounded-xl bg-ai/[0.045] px-3 py-3 ring-1 ring-inset ring-ai/10">
                <span className="text-[11px] font-bold text-ai">{n}</span>
                <strong className="ml-1.5 text-[13px] text-ink">{title}</strong>
                <span className="mt-1 block text-[11.5px] leading-snug text-ink-subtle">{desc}</span>
              </li>
            ))}
          </ol>
        </Reveal>

        {/* A settled, explicitly illustrative Aira run above the tabs. */}
        <Reveal className="flex justify-center mt-7" amount={0.2}>
          <AiraTicker lines={ticker} />
        </Reveal>

        {/* persona explorer — tabs + panel (the AI sibling of the #modules explorer) */}
        <Reveal className="mt-6" amount={0.1}>
          {/* tabs: horizontally scrollable snap chips on mobile, centered wrap on desktop */}
          <div role="tablist" aria-label="Aira for every role"
            className="flex gap-2 overflow-x-auto snap-x pb-1.5 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0">
            {PERSONA_TABS.map((t, i) => {
              const on = tab === t.k;
              const Icon = t.icon;
              return (
                <button key={t.k} type="button" role="tab" id={`aitab-${t.k}`} aria-selected={on} aria-controls="ai-suite-panel"
                  tabIndex={on ? 0 : -1} ref={(el) => (tabRefs.current[i] = el)}
                  onKeyDown={(e) => onTabKey(e, i)} onClick={() => selectTab(t.k)}
                  data-testid={`aisuite-tab-${t.k}`}
                  className={cn(
                    "relative shrink-0 snap-start inline-flex items-center gap-2 h-11 px-4 sm:px-5 rounded-full text-[13.5px] sm:text-[14px] font-semibold whitespace-nowrap transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
                    on ? "text-white" : "bg-surface border border-line text-ink-2 hover:border-ai/40 hover:text-ink hover:bg-ai/[0.05]"
                  )}>
                  {/* sliding active indicator — layout-projected (transform only) */}
                  {on && (
                    <m.span layoutId="aiTabPill" className="absolute inset-0 rounded-full bg-ai shadow-[0_5px_16px_rgba(124,58,237,0.35)]"
                      transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 360, damping: 30 }} aria-hidden="true" />
                  )}
                  <Icon className={cn("relative z-10 w-4 h-4", on ? "text-white" : "text-ai")} aria-hidden="true" />
                  <span className="relative z-10">{t.label}</span>
                </button>
              );
            })}
          </div>

          {/* panel shell — purple hairline on top, mirrors the modules card surface */}
          <div className="relative mt-5 rounded-[28px] border border-ai/15 bg-ai/[0.03] p-5 sm:p-6 lg:p-8">
            <div className="absolute left-8 right-8 top-0 h-[3px] rounded-full bg-[linear-gradient(90deg,transparent,#7C3AED,transparent)] opacity-40" aria-hidden="true" />

            <div id="ai-suite-panel" role="tabpanel" aria-labelledby={`aitab-${tab}`}
              className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_290px] gap-6 items-start">
              <div className="min-w-0">
                {/* per-tab factual number strip — every figure grounded in §13/§16 */}
                <m.p key={tab + "-strip"} data-testid="aisuite-strip"
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-4 text-[12px] font-semibold text-ai-ink"
                  initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: EASE }}>
                  <Sparkles className="w-3.5 h-3.5 text-ai shrink-0" aria-hidden="true" />
                  {active.strip.map((s, i) => (
                    <React.Fragment key={s}>
                      {i > 0 && <span className="text-ai/40" aria-hidden="true">·</span>}
                      <span>{s}</span>
                    </React.Fragment>
                  ))}
                  <span className="font-normal text-ink-subtle">— tap any card for how it works</span>
                </m.p>

                {/* card grid — staggered fade-up on every tab change (~60ms apart, 200ms
                    ease-out); items-start so an expanded card never stretches its row */}
                <m.div key={tab + "-grid"}
                  className={cn("grid grid-cols-1 sm:grid-cols-2 gap-4 items-start", active.cols === 3 && "lg:grid-cols-3")}
                  variants={reduce ? undefined : PANEL_STAGGER} initial={reduce ? false : "hidden"} animate="show">
                  {active.cards.map((card, i) => {
                    const rid = `aicard-panel-${active.k}-${i}`;
                    return (
                      <AiCard key={card.title} card={card} reduce={reduce} rid={rid}
                        testid={`aisuite-card-${active.k}-${i}`}
                        open={expanded === card.title}
                        onToggle={() => setExpanded((p) => (p === card.title ? null : card.title))} />
                    );
                  })}
                </m.div>

                {/* automations tab extra — example starter-rule chips */}
                {tab === "automations" && (
                  <m.div key="auto-extras" className="mt-5"
                    initial={reduce ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: EASE, delay: reduce ? 0 : 0.2 }}>
                    <p className="text-ink-subtle text-[12px] font-semibold uppercase tracking-wide mb-2.5">A few of the 40 starter rules</p>
                    <ul className="flex flex-wrap gap-2" aria-label="Examples of the 40 ready automations">
                      {AUTOMATION_EXAMPLES.map((t) => (
                        <li key={t} className="inline-flex items-center rounded-full bg-surface border border-line text-ink-2 text-[12.5px] font-medium px-3 py-1.5">{t}</li>
                      ))}
                    </ul>
                  </m.div>
                )}
              </div>

              {/* per-persona guardrails rail */}
              <m.aside key={tab + "-rail"}
                initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: EASE, delay: reduce ? 0 : 0.15 }}>
                <GuardrailsRail rail={active.rail} />
              </m.aside>
            </div>
          </div>
        </Reveal>

        {/* trust footnotes */}
        <Reveal className="mt-10 border-t border-line pt-6" amount={0.2}>
          <ul className="grid sm:grid-cols-3 gap-x-8 gap-y-3">
            {TRUST_NOTES.map(([Icon, t]) => (
              <li key={t} className="flex items-start gap-2.5">
                <Icon className="w-4 h-4 text-ai shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-ink-subtle text-[13px] leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* CTA */}
        <Reveal className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10" amount={0.2}>
          {seeAiraVisible && (
            <Button variant="ai" onClick={() => smoothScrollToId("aira-ai")} data-testid="aisuite-see-aira" className="justify-center">
              See Aira answer a fee question <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Button>
          )}
          <Button variant="outline" onClick={onRegister || (() => smoothScrollToId("register-your-school"))} data-testid="aisuite-register" className="justify-center">
            {CTA_PRIMARY_LABEL}
          </Button>
        </Reveal>
      </div>
    </section>
  );
}

// PERF-6: the persona tab pill is a SHARED-LAYOUT animation (`layoutId`), which needs framer-motion's
// layout-projection engine — that lives in `domMax`, not in the `domAnimation` bundle MarketingMotion
// loads for the eager marketing entry chunk. This section is route-lazy (only /aira renders it), so it
// mounts its own nested LazyMotion: the projection code lands in THIS async chunk and the landing
// entry chunk keeps the small bundle. Nesting is additive — framer-motion's feature registry is global,
// so `domMax` simply adds drag+layout on top of whatever is already loaded.
export default function AiSuiteSection(props) {
  return (
    <LazyMotion features={domMax} strict>
      <AiSuiteSectionContent {...props} />
    </LazyMotion>
  );
}
